import { useState, useContext, useEffect } from "react";

import DataTable from "@shared-ui-components/DataTable";

import LocationContext from "@context/location/locationContext";
import {
  Employee,
  RevCenter,
  Store,
} from "@shared-ui-components/DataTable/types";

import { getCustomExtraPath } from "@pages/Dashboard/RevenueDashBoard/TotalSales/helper";
import { useNavigate } from "react-router-dom";
import { getApiResponse } from "@src/utils/service";
import { SORTKEYMAP } from "@src/constants/DataTable";
import { message } from "@src/constants";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { columns, filterOptions, refineData } from "./helper";

const RevenueBreakdownTable = ({ pagination }: { pagination: boolean }) => {
  const [type, setType] = useState("");
  const [dyColumns, setDyColumns] = useState(columns);
  const [isLoading, setIsLoading] = useState(true);
  const [rowData, setRowsData] = useState<Store[] | RevCenter[] | Employee[]>(
    []
  );
  const [typeOptions, setTypeOptions] = useState([
    { label: "By Payment Type", value: "pay" },
    { label: "By Employee", value: "emp" },
  ]);

  const [pageNumber, setPageNumber] = useState(0);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState({
    tableName: "revenue",
    label: "Total Sales",
    order: "desc",
  });
  const [{ locationState }] = useContext(LocationContext);

  const { property, store, revenueCenter, date } = locationState;
  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const filterType = filterOptions.find((el) => el.label === type)?.value;

  const filters = {
    property,
    store,
    rvc: revenueCenter,
    date,
    filterBy: filterType || "",
  };

  const filter = getCustomExtraPath(filters);

  const url = `sales-breakdown${filter}&sort_by=${
    sortBy.tableName
  }&sort_order=${sortBy.order === "desc" ? "desc" : "asc"}&page=${
    pageNumber + 1
  }`;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage - 1);
  };

  const setLocalFilter = (changedtype: string, page: number) => {
    localStorage.setItem(
      "RevenueFilter",
      JSON.stringify({ filtertype: changedtype, pageNumber: page })
    );
    setType(changedtype);
  };

  const handleDyColumns = (changedType: string) => {
    if (changedType.toLowerCase() === "by store") {
      setDyColumns((prev) => {
        return [
          { id: "name", label: "Store", minWidth: 145 },
          prev[prev.length - 1],
        ];
      });
    }
    if (changedType.toLowerCase() === "by rev center") {
      setDyColumns((prev) => {
        return [
          { id: "name", label: "Rev Center", minWidth: 145 },
          prev[prev.length - 1],
        ];
      });
    }
    if (changedType.toLowerCase() === "by payment type") {
      setDyColumns((prev) => {
        return [
          { id: "name", label: "Payment Type", minWidth: 145 },
          prev[prev.length - 1],
        ];
      });
    }
    if (changedType.toLowerCase() === "by employee") {
      setDyColumns((prev) => {
        return [
          { id: "name", label: "Employee", minWidth: 145 },
          prev[prev.length - 1],
        ];
      });
    }
  };

  const localRevenueFilter = localStorage.getItem("RevenueFilter");

  const handleTypeChange = (changedType: string) => {
    setRowsData([]);
    setIsLoading(true);
    setPageNumber(0);
    handleDyColumns(changedType);
    setType(changedType);
    setLocalFilter(changedType, pageNumber);
  };

  const sortHandler = (sortkey: string) => {
    const sortKeyObject = SORTKEYMAP.find(
      (el) => el.tableName === sortkey.toLowerCase()
    );
    setSortBy((prev) => {
      if (sortKeyObject) {
        return {
          tableName: sortKeyObject.label,
          label: sortkey,
          order: prev.order === "desc" ? "asc" : "desc",
        };
      }
      return {
        tableName: "name",
        label: sortkey,
        order: prev.order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handleNavigate = () => {
    navigate("/revenue-dashboard/sales-breakdown");
  };

  const handleLocalFilters = () => {
    if (localRevenueFilter !== null) {
      const localFilters = JSON.parse(localRevenueFilter);
      const localFilterType =
        localFilters?.filtertype?.length > 0
          ? localFilters.filtertype
          : "By Store";
      setType(localFilterType);
      handleDyColumns(localFilterType);

      if (property?.length > 0 && store === "" && revenueCenter === "") {
        setTypeOptions((prev) => {
          return [
            { label: "By Store", value: "store" },
            { label: "By Rev Center", value: "rvc" },
            ...prev,
          ];
        });
      }
      if (property?.length > 0 && store?.length > 0 && revenueCenter === "") {
        if (localFilters.filtertype === "") {
          setLocalFilter("By Rev Center", pageNumber);
        }
        if (localFilters?.filtertype === "By Store") {
          setType("By Rev Center");
        }
        setTypeOptions((prev) => {
          return [{ label: "By Rev Center", value: "rvc" }, ...prev];
        });
      }
      if (
        property?.length > 0 &&
        store?.length > 0 &&
        revenueCenter?.length > 0
      ) {
        if (
          localFilters.filtertype === "" ||
          localFilters.filtertype === "By Store"
        ) {
          setLocalFilter("By Payment Type", pageNumber);
        }
      }
    } else {
      setType("By Store");
      setLocalFilter("By Store", pageNumber);
    }
  };

  useEffect(() => {
    setPageNumber(0);
    handleLocalFilters();
    return () => {
      setTypeOptions([
        { label: "By Payment Type", value: "pay" },
        { label: "By Employee", value: "emp" },
      ]);
    };
  }, [property, store, revenueCenter, date, localRevenueFilter]);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const result = await getApiResponse(url);
        if (result?.data !== null) {
          setRowsData(result?.data);
          setIsLoading(false);
        } else {
          showErrorToastSetState({
            errorMessage: result?.error || "",
            errorTitle: message.ERROR_MESSAGE_DASHBOARD,
            open: true,
          });
        }
        setIsLoading(false);
      } catch (err) {
        showErrorToastSetState({
          errorMessage: "",
          errorTitle: message.ERROR_MESSAGE_DASHBOARD,
          open: true,
        });
      }
    };
    if (filterType !== undefined) {
      getData();
    }
  }, [url, filterType]);

  return (
    <DataTable
      sortHandler={sortHandler}
      title="Sales Breakdown"
      rowData={refineData(rowData)}
      page={pageNumber}
      showPagination={pagination}
      sortBy={sortBy}
      handleNavigate={handleNavigate}
      handlePageChange={handleChangePage}
      handleTypeChange={handleTypeChange}
      type={type}
      isLoading={isLoading}
      dyColumns={dyColumns}
      dropDownOptions={typeOptions}
    />
  );
};

export default RevenueBreakdownTable;
